// Independent reservations retain the timeline move and resize interactions.
import { useNavigate } from "@tanstack/react-router";
import { CASE_TITLE_MAX } from "@trameria/core/cases-shared";
import type { TimeBlock } from "@trameria/core/timeblocker-shared";
import { m } from "@trameria/i18n";
import {
  type PointerEvent as ReactPointerEvent,
  useRef,
  useState,
} from "react";
import { dateIn } from "../cases/dates";
import { apiErrorMessage } from "../data/errors";
import { isOptimisticId } from "../data/optimistic";
import { ActionCheck } from "../ui/action-check";
import { Button, IconButton } from "../ui/button";
import { Icon } from "../ui/icon";
import { isImeComposing } from "../ui/ime";
import { Modal } from "../ui/modal";
import { TimeField } from "../ui/time-field";
import {
  blockLabel,
  clock,
  DAY_MINUTES,
  type DragMode,
  dragRange,
  isoAt,
  minutesInDay,
  minutesOfValue,
  PX_PER_MIN,
  rawMinutes,
  SNAP_MINUTES,
  snapMinutes,
  timeValueOf,
} from "./day";
import { LinkPicker } from "./link-picker";
import {
  useLinkBlock,
  useMoveBlock,
  useRemoveBlock,
  useUpdateAction,
} from "./queries";
import { StartEndButton } from "./running";
import { useIsCarriedOver, useTimeBlockerScreen } from "./screen-state";
import { WorkControl } from "./work-control";
import { workingLinks } from "./works";

/** A pointer moving this far is a drag, not a press. */
const PRESS_SLOP = 3;

export function TimeBlockBox({
  block,
  column,
  columnCss,
  lead,
}: {
  block: TimeBlock;
  column: { col: number; cols: number };
  columnCss(
    c: { col: number; cols: number },
    lead: number,
  ): { left: string; width: string };
  /** The lane's room at its left (the work bars). */
  lead: number;
}) {
  const screen = useTimeBlockerScreen();
  const { dayStart } = screen;
  const move = useMoveBlock();
  const remove = useRemoveBlock();
  const onError = (err: unknown) =>
    screen.setError(apiErrorMessage(err, m.timeblocker_save_failed()));
  const start = minutesInDay(block.startAt, dayStart);
  const end = minutesInDay(block.endAt, dayStart);
  const pending = isOptimisticId(block.id);
  const fixed =
    pending ||
    rawMinutes(block.startAt, dayStart) < 0 ||
    rawMinutes(block.endAt, dayStart) > DAY_MINUTES;
  const origin = useRef<{
    x: number;
    y: number;
    mode: DragMode;
    moved: boolean;
  } | null>(null);
  const [drag, setDrag] = useState<{ start: number; end: number } | null>(null);
  const s = drag?.start ?? start;
  const e = drag?.end ?? end;
  const open = !block.endedAt;
  // Every linked Case, side by side; the line cuts only where it meets the button.
  const linkedCases = [...new Set(block.links.map((link) => link.caseTitle))]
    .filter((title) => title !== blockLabel(block))
    .join(" · ");
  const actionCase = linkedCases || undefined;
  // What is being worked on now inside this Block (started by hand in its detail; several at once).
  const working = workingLinks(block, screen.now.getTime());
  // A card carried over this block goes into it when let go.
  const joining = useIsCarriedOver(block.id);
  // The card pressed in the list lights the blocks linked to it for a moment.
  const f = screen.focus;
  const lit =
    !!f &&
    block.links.some(
      (l) =>
        l.caseId === f.caseId &&
        (f.stepId === null ? !l.stepId : l.stepId === f.stepId),
    );

  const removeIt = () => remove.mutate({ ...screen.place, block }, { onError });

  const onPointerDown = (mode: DragMode) => (ev: ReactPointerEvent) => {
    if (ev.button !== 0) return;
    if ((ev.target as Element).closest("button, input")) return;
    ev.stopPropagation();
    ev.currentTarget.setPointerCapture(ev.pointerId);
    origin.current = {
      x: ev.clientX,
      y: ev.clientY,
      mode,
      moved: false,
    };
  };
  const onPointerMove = (ev: ReactPointerEvent) => {
    const o = origin.current;
    if (!o) return;
    // A block that cannot move still does not open when pressed and moved.
    if (
      Math.abs(ev.clientY - o.y) > PRESS_SLOP ||
      Math.abs(ev.clientX - o.x) > PRESS_SLOP
    )
      o.moved = true;
    if (!o.moved || fixed) return;
    setDrag(
      dragRange(
        o.mode,
        start,
        end,
        snapMinutes((ev.clientY - o.y) / PX_PER_MIN),
      ),
    );
  };
  const finish = (send: boolean) => {
    const o = origin.current;
    origin.current = null;
    if (!o) return;
    const next = drag;
    setDrag(null);
    if (!send) return;
    if (!o.moved) {
      if (o.mode === "move") screen.setSelected(block.id);
      return;
    }
    if (!next || (next.start === start && next.end === end)) return;
    move.mutate(
      {
        ...screen.place,
        block,
        startAt: isoAt(dayStart, next.start),
        endAt: isoAt(dayStart, next.end),
      },
      { onError },
    );
  };
  const handlers = {
    onPointerMove,
    onPointerUp: () => finish(true),
    onPointerCancel: () => finish(false),
  };
  const height = (e - s) * PX_PER_MIN;
  const tall = height >= 36;
  return (
    // biome-ignore lint/a11y/useSemanticElements: it holds the edges that resize it, so it cannot be a button
    <div
      role="button"
      tabIndex={0}
      data-axis-item=""
      data-block-id={block.id}
      data-block-start={block.startAt}
      aria-label={m.timeblocker_block_label({
        title: actionCase
          ? `${actionCase} · ${blockLabel(block)}`
          : blockLabel(block),
        start: clock(s),
        end: clock(e),
      })}
      onKeyDown={(ev) => {
        if (ev.target !== ev.currentTarget) return;
        if (ev.key === "Enter" || ev.key === " ") {
          ev.preventDefault();
          screen.setSelected(block.id);
        } else if (
          (ev.key === "Delete" || ev.key === "Backspace") &&
          !pending
        ) {
          // The keyboard's way to carry it to the list.
          ev.preventDefault();
          removeIt();
        }
      }}
      onPointerDown={onPointerDown("move")}
      {...handlers}
      className={`group/block absolute flex touch-pan-y select-none overflow-hidden rounded-[6px] border-l-[3px] px-2 text-[12px] leading-[16px] transition-shadow duration-300 ${
        lit || joining ? "ring-2 ring-accent" : ""
      } ${
        open
          ? "border-accent bg-chosen text-fg"
          : "border-pill-border bg-bubble text-body-soft"
      } ${fixed ? "cursor-pointer" : "cursor-grab active:cursor-grabbing"} ${
        drag ? "z-20 shadow-sm" : ""
      }`}
      style={{
        top: s * PX_PER_MIN,
        height: Math.max(height, 12),
        ...columnCss(column, lead),
      }}
    >
      <div
        className={`min-w-0 flex-1 ${tall ? "py-[3px]" : "flex items-center gap-2"}`}
      >
        <div className="min-w-0 flex-1 truncate">{blockLabel(block)}</div>
        <div
          className={`flex min-w-0 items-center gap-1 text-[11px] tabular-nums text-chosen-fg ${
            tall ? "" : "max-w-[60%]"
          }`}
        >
          <span className="flex-none">
            {clock(s)}–{clock(e)}
          </span>
          {working.length > 0 ? (
            <span className="flex min-w-0 items-center gap-1 truncate text-accent-deep">
              <span
                aria-hidden="true"
                className="h-[5px] w-[5px] flex-none animate-pulse rounded-full bg-accent"
              />
              <span className="min-w-0 truncate">
                {working[0].stepTitle ?? working[0].caseTitle}
              </span>
              {working.length > 1 && (
                <span className="flex-none">+{working.length - 1}</span>
              )}
            </span>
          ) : (
            actionCase && (
              <span className="min-w-0 truncate text-faint">{actionCase}</span>
            )
          )}
        </div>
      </div>
      {joining && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex items-center justify-center bg-accent/15 text-[12px] font-medium text-accent-deep"
        >
          {m.timeblocker_join_here()}
        </div>
      )}
      {height >= 20 && (
        <span
          className={`ml-2 flex flex-none ${tall ? "items-start pt-[1px]" : "items-center"}`}
        >
          <StartEndButton block={block} onError={onError} />
        </span>
      )}
      {!fixed && (
        <>
          <div
            aria-hidden="true"
            onPointerDown={onPointerDown("top")}
            {...handlers}
            className="absolute inset-x-0 top-0 h-[5px] cursor-ns-resize touch-none"
          />
          <div
            aria-hidden="true"
            onPointerDown={onPointerDown("bottom")}
            {...handlers}
            className="absolute inset-x-0 bottom-0 h-[5px] cursor-ns-resize touch-none"
          />
        </>
      )}
    </div>
  );
}

const FIELD =
  "rounded-[6px] border border-field bg-surface px-2 py-[3px] text-fg disabled:opacity-60";

/** The independent reservation and every linked Case or Action. */
export function BlockDetail({
  block,
  onClose,
}: {
  block: TimeBlock;
  onClose(): void;
}) {
  const screen = useTimeBlockerScreen(),
    navigate = useNavigate();
  const move = useMoveBlock(),
    remove = useRemoveBlock(),
    link = useLinkBlock(),
    update = useUpdateAction();
  const [error, setError] = useState<string | null>(null);
  const onError = (e: unknown) =>
    setError(apiErrorMessage(e, m.timeblocker_save_failed()));
  const linkedCases = new Map<string, typeof block.links>();
  for (const target of block.links) {
    const links = linkedCases.get(target.caseId) ?? [];
    links.push(target);
    linkedCases.set(target.caseId, links);
  }
  const pending = isOptimisticId(block.id);
  const start = rawMinutes(block.startAt, screen.dayStart),
    end = rawMinutes(block.endAt, screen.dayStart);
  const setTime = (s: number, e: number) => {
    if (e - s < SNAP_MINUTES || e - s > DAY_MINUTES) return;
    move.mutate(
      {
        ...screen.place,
        block,
        startAt: isoAt(screen.dayStart, s),
        endAt: isoAt(screen.dayStart, e),
      },
      { onError },
    );
  };
  return (
    <Modal
      open
      onClose={onClose}
      label={m.timeblocker_detail()}
      placement="center"
      className="p-5!"
    >
      <div className="flex items-center gap-2">
        <TitleField
          key={block.title}
          initial={block.title ?? ""}
          label={m.timeblocker_title_label()}
          maxLength={1000}
          onCommit={(title) =>
            move.mutate(
              { ...screen.place, block, title: title || null },
              { onError },
            )
          }
        />
        <IconButton label={m.timeblocker_close()} onClick={onClose}>
          <Icon kind="close" />
        </IconButton>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2 text-[13px] tabular-nums">
        <TimeField
          label={m.timeblocker_start()}
          value={timeValueOf((start + DAY_MINUTES) % DAY_MINUTES)}
          timeZone={screen.timeZone}
          disabled={pending}
          className={FIELD}
          onChange={(v) => {
            const at = minutesOfValue(v);
            if (at !== null) {
              const s = Math.floor(start / DAY_MINUTES) * DAY_MINUTES + at;
              setTime(s, s + end - start);
            }
          }}
        />
        –
        <TimeField
          label={m.timeblocker_end()}
          value={timeValueOf(end % DAY_MINUTES)}
          timeZone={screen.timeZone}
          disabled={pending}
          className={FIELD}
          onChange={(v) => {
            const at = minutesOfValue(v);
            if (at !== null) {
              let e = Math.floor(start / DAY_MINUTES) * DAY_MINUTES + at;
              if (e <= start) e += DAY_MINUTES;
              setTime(start, e);
            }
          }}
        />
        <span className="ml-auto">
          <StartEndButton block={block} size="large" />
        </span>
      </div>
      <ul className="my-4 flex flex-col gap-2">
        {[...linkedCases].map(([caseId, links]) => (
          <li key={caseId}>
            <button
              type="button"
              className="mb-1 flex max-w-full items-center gap-1 text-left text-[12px] text-muted hover:underline"
              onClick={() => {
                onClose();
                void navigate({ to: `/me/cases/${caseId}` });
              }}
            >
              <Icon kind="cases" size={13} className="flex-none" />
              <span className="sr-only">{m.timeblocker_kind_case()}</span>
              <span className="min-w-0 truncate">{links[0].caseTitle}</span>
            </button>
            <ul className="flex flex-col gap-2 pl-3">
              {links.map((l) => (
                <li
                  key={`${l.caseId}:${l.stepId}`}
                  className="group/row flex items-center gap-2 text-[13px]"
                >
                  {l.stepId && (
                    <ActionCheck
                      checked={l.status === "done"}
                      aria-label={m.timeblocker_mark_done_of({
                        title: l.stepTitle ?? l.caseTitle,
                      })}
                      onChange={() =>
                        update.mutate(
                          {
                            ...screen.place,
                            action: {
                              caseId: l.caseId,
                              id: l.stepId as string,
                            },
                            patch: {
                              status: l.status === "done" ? "open" : "done",
                            },
                          },
                          { onError },
                        )
                      }
                    />
                  )}
                  {/* A whole Case has no check: keep its column so the icons line up. */}
                  {!l.stepId && (
                    <span aria-hidden="true" className="w-4 flex-none" />
                  )}
                  <Icon
                    kind={l.stepId ? "action" : "cases"}
                    size={15}
                    className="flex-none text-muted"
                  />
                  <span className="sr-only">
                    {l.stepId
                      ? m.timeblocker_kind_action()
                      : m.timeblocker_kind_case()}
                  </span>
                  <button
                    type="button"
                    className="min-w-0 flex-1 truncate text-left hover:underline"
                    onClick={() => {
                      onClose();
                      void navigate({ to: `/me/cases/${l.caseId}` });
                    }}
                  >
                    {l.stepTitle ?? m.timeblocker_case_whole()}{" "}
                    <span className="ml-2 text-[11px] text-faint">
                      {l.status === "done"
                        ? m.timeblocker_done()
                        : m.timeblocker_todo()}
                    </span>
                  </button>
                  <WorkControl block={block} link={l} onError={onError} />
                  <IconButton
                    label={m.timeblocker_unlink()}
                    onClick={() =>
                      link.mutate(
                        {
                          ...screen.place,
                          blockId: block.id,
                          target: l,
                          remove: true,
                        },
                        { onError },
                      )
                    }
                  >
                    <Icon kind="close" size={12} />
                  </IconButton>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
      {!pending && (
        <LinkPicker
          blockId={block.id}
          planOn={dateIn(block.startAt, screen.timeZone)}
        />
      )}
      {error && (
        <p role="alert" className="mt-2 text-[12px] text-danger">
          {error}
        </p>
      )}
      <Button
        variant="danger"
        disabled={pending}
        className="mt-4"
        onClick={() =>
          remove.mutate(
            { ...screen.place, block },
            {
              onError: (e) =>
                screen.setError(
                  apiErrorMessage(e, m.timeblocker_save_failed()),
                ),
            },
          )
        }
      >
        {m.timeblocker_block_remove()}
      </Button>
    </Modal>
  );
}

/**
 * A title written in place: no frame, sent on blur or Enter when changed and
 * not empty (an empty one goes back to what it was).
 */
export function TitleField({
  initial,
  label,
  maxLength = CASE_TITLE_MAX,
  onCommit,
}: {
  initial: string;
  label: string;
  maxLength?: number;
  onCommit(value: string): void;
}) {
  const [value, setValue] = useState(initial);
  const commit = () => {
    const next = value.trim();
    if (next !== initial) onCommit(next);
  };
  return (
    <input
      value={value}
      placeholder="Block"
      aria-label={label}
      maxLength={maxLength}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !isImeComposing(e.nativeEvent)) {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      className="plain-field w-full border-none bg-transparent p-0 text-[17px] leading-[26px] text-fg outline-none"
    />
  );
}
